/**
 * Shared pure-math scene-geometry builder for the SPLINE entity (DEFUSE
 * Direction-3 — clone elimination).
 *
 * Takes the ALREADY-PARSED primitive inputs (raw pre-offset control-point
 * `Vec3[]`, the knot `number[]`, the degree, and the optional start/end
 * tangent `Vec3` pair) and returns the `{ geometry, bounds }` pair that both
 * the legacy `../splineToRenderable.ts` (reads the `EntityPreview.properties`
 * string bag) and the typed `../typed/splineFromEntity.ts` (reads the
 * canonical `SplineEntity` fields) feed into `buildRenderableShell`.
 *
 * Why this module exists: after value extraction the two paths were
 * byte-identical — the SAME `applyOffset` on the control points, the SAME
 * conditional `tangentVectors` shaping, the SAME control-polygon-hull bbox.
 * That is one semantic truth (the spline render-geometry math), duplicated
 * only because the two paths differ in their value source. The math lives
 * here ONCE.
 *
 * Coordinate origin:
 *   - Control points are absolute world positions → run through `applyOffset`
 *     (exactly as both siblings did).
 *   - Tangent vectors are raw DELTAS (DXF group codes 12 / 13 are relative to
 *     the first / last fit point), NOT world points, so they are NOT offset —
 *     matching both siblings.
 *
 * # CLAUDE.md priority #1 — cubic Bezier only
 *   `tangentVectors` (DXF 12 / 13) are preserved EXPLICITLY on the geometry,
 *   never quadratic, never flattened to a straight polyline. The renderer
 *   tessellates the cubic via `buildSplineBeziers`.
 *
 * Bbox is the control-polygon hull (a true Bezier curve lies inside its
 * control hull, so this is a conservative pre-filter for the spatial index) —
 * identical to both siblings.
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`.
 */

import { applyOffset } from "../../coordinateOrigin";
import type { SceneGeometry, Vec3 } from "../../types";

import { boundsFromPoints } from "../adapterContext";

/** AABB shape carried on `RenderableEntity.bounds`. */
export interface SplineSceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** `{ geometry, bounds }` pair the builder returns. */
export interface SplineSceneGeometryResult {
  geometry: SceneGeometry;
  bounds: SplineSceneBounds;
}

/**
 * Build the `spline` geometry + AABB from the raw (pre-offset) control points,
 * the knot vector, the degree, and the optional start/end tangent pair.
 *
 * `applyOffset` is applied to the control points (absolute world positions);
 * the tangent vectors are raw deltas and pass through unchanged.
 */
export function buildSplineSceneGeometry(
  controlPointsRaw: Vec3[],
  knots: number[],
  degree: number,
  tangentVectors: { start: Vec3; end: Vec3 } | undefined,
): SplineSceneGeometryResult {
  const controlPoints = controlPointsRaw.map(applyOffset);

  const geometry: SceneGeometry = tangentVectors
    ? {
        kind: "spline",
        controlPoints,
        knots,
        degree,
        tangentVectors,
      }
    : {
        kind: "spline",
        controlPoints,
        knots,
        degree,
      };

  // Conservative bbox = control-polygon hull (the Bezier curve is contained
  // within its control hull). Empty control set → a degenerate origin point,
  // matching both legacy + typed siblings.
  const bounds = boundsFromPoints(
    controlPoints.length > 0 ? controlPoints : [{ x: 0, y: 0, z: 0 }],
  );

  return { geometry, bounds };
}
